'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, FormGroup, FormInputErrors } from '~/components/primitives';
import { Input } from '~/components/primitives';
import Link, { useLinkStatus } from 'next/link';
import { LoginBackgroundImage } from '~/components/layout/images';
import LogoBizz from '~/app/[locale]/bizz/LogoBizz';
import { action_getBizzUserLoginType } from './actions';
import { useFormState } from '~/modules/form';
import { useI18n } from '~/features/i18n';
import useNotifications from '~/features/notifications/useNotifications';
import { useRouter } from 'next/navigation';
import { useSignInMutation } from '~/features/auth/login';
import { Auth_loginUrlWithRedirect } from '~/modules/auth/redirect';
import { Bizz_LoginMethod } from './shared';
import { Data } from 'effect';
import { useTaggedEnumState } from '~/modules/effect/state';
import { samlSignInVendor } from '@funkey/api/src/features/auth/ory-polis/shared';
import { buttonVariant } from '~/components/primitives/Button/shared';
import { Loader2 } from 'lucide-react';
const BizzCredentialsLoginPage: React.FC<{
  onSignInRedirectURL: string;
  /** Already validated server-side; forwarded so the SAML flow lands on the requested page too. */
  redirectUrl: string | null;
}> = props => {
  const $ = _c(41);
  const i18n = useI18n();
  const router = useRouter();
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      email: "",
      password: ""
    };
    t1 = {
      email: {
        validate: _temp,
        message: "Email is required."
      },
      password: {
        validate: _temp2,
        message: "Password is required."
      }
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  const formState = useFormState(t0, t1);
  const notificationCtx = useNotifications();
  const step = useTaggedEnumState(_temp3);
  let t2;
  if ($[2] !== i18n.locale || $[3] !== props.onSignInRedirectURL) {
    t2 = props.onSignInRedirectURL ?? {
      locale: i18n.locale
    };
    $[2] = i18n.locale;
    $[3] = props.onSignInRedirectURL;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = {
      callbackURL: t2
    };
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const signIn = useSignInMutation(t3);
  let t4;
  if ($[7] !== i18n.locale || $[8] !== props.redirectUrl) {
    t4 = function samlHref(saml, connectionId) {
      return Auth_loginUrlWithRedirect(`/${i18n.locale}/bizz?saml-login=${saml.bizz.slug}${connectionId ? `&idp=${connectionId}` : ""}`, props.redirectUrl);
    };
    $[7] = i18n.locale;
    $[8] = props.redirectUrl;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const samlHref = t4;
  let t5;
  if ($[10] !== router || $[11] !== samlHref) {
    t5 = function navigateToSaml(saml_0, connectionId_0) {
      router.push(samlHref(saml_0, connectionId_0));
    };
    $[10] = router;
    $[11] = samlHref;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const navigateToSaml = t5;
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <LoginBackgroundImage theme="black" />;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <LogoBizz className="mx-auto mb-24 h-11 w-24" />;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== i18n) {
    t8 = i18n._("Log in");
    $[15] = i18n;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t8) {
    t9 = <h1 className="mb-10 text-3xl font-extrabold leading-8 text-blue-900">{t8}</h1>;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== formState || $[20] !== i18n || $[21] !== navigateToSaml || $[22] !== notificationCtx || $[23] !== samlHref || $[24] !== signIn || $[25] !== step) {
    let t11;
    if ($[27] !== formState || $[28] !== i18n || $[29] !== navigateToSaml || $[30] !== notificationCtx || $[31] !== step) {
      t11 = () => <form onSubmit={async e => {
        e.preventDefault();
        if (!formState.values.email.length) {
          notificationCtx.dispatchError({
            message: i18n._("Invalid email")
          });
          return;
        }
        const methods = await action_getBizzUserLoginType(formState.values.email);
        const saml_1 = methods.find(Bizz_LoginMethod.$is("saml")) ?? null;
        const hasCredentials = methods.some(Bizz_LoginMethod.$is("credentials"));
        const canSkipPassword = !!saml_1 && !saml_1.inferredFromEmailDomain && !hasCredentials && saml_1.connections.length === 1;
        if (canSkipPassword) {
          navigateToSaml(saml_1, saml_1.connections[0]?.id);
        } else {
          step.setFull({
            saml: saml_1
          });
        }
      }}><FormGroup><Input placeholder={i18n._("Email")} {...formState.getNativeInputProps("email")} /><FormInputErrors errors={formState.getErrorMessages("email")} /></FormGroup><Button type="submit" size="lg" variant="tertiary" className="w-full">{i18n._("Next step")}</Button></form>;
      $[27] = formState;
      $[28] = i18n;
      $[29] = navigateToSaml;
      $[30] = notificationCtx;
      $[31] = step;
      $[32] = t11;
    } else {
      t11 = $[32];
    }
    let t12;
    if ($[33] !== formState || $[34] !== i18n || $[35] !== samlHref || $[36] !== signIn) {
      t12 = data => {
        const saml_2 = data.saml;
        return <form onSubmit={async e_0 => {
          e_0.preventDefault();
          if (!formState.validate()) {
            return;
          }
          return signIn.mutateAsync(formState.values);
        }}><FormGroup><Input placeholder={i18n._("Email")} {...formState.getNativeInputProps("email")} /><FormInputErrors errors={formState.getErrorMessages("email")} /></FormGroup><FormGroup><Input placeholder={i18n._("Password")} {...formState.getNativeInputProps("password")} autoFocus={true} type="password" /><FormInputErrors errors={formState.getErrorMessages("password")} /></FormGroup><menu className="flex flex-wrap items-center justify-between gap-4"><Link href={`/${i18n.locale}/forgot-password`} className="block text-sm font-medium text-turqoise-500">{i18n._("Forgot password?")}</Link><Button type="submit" variant="tertiary" size="lg" isLoading={signIn.isPending}>{i18n._("Sign in")}</Button></menu>{saml_2 && <><div className="my-6 flex items-center gap-4"><div className="h-px flex-1 bg-gray-200" /><span className="text-sm uppercase text-gray-400">{i18n._("or")}</span><div className="h-px flex-1 bg-gray-200" /></div><div className="flex flex-col gap-3">{saml_2.connections.length ? saml_2.connections.map(connection => <SamlButton key={connection.id} bizz={saml_2.bizz} label={saml_2.connections.length > 1 ? connection.label : null} vendor={samlSignInVendor(connection.idpEntityId, connection.provider)} href={samlHref(saml_2, connection.id)} />) : <SamlButton bizz={saml_2.bizz} href={samlHref(saml_2)} />}</div></>}</form>;
      };
      $[33] = formState;
      $[34] = i18n;
      $[35] = samlHref;
      $[36] = signIn;
      $[37] = t12;
    } else {
      t12 = $[37];
    }
    t10 = step.$match({
      Email: t11,
      Full: t12
    });
    $[19] = formState;
    $[20] = i18n;
    $[21] = navigateToSaml;
    $[22] = notificationCtx;
    $[23] = samlHref;
    $[24] = signIn;
    $[25] = step;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[38] !== t10 || $[39] !== t9) {
    t11 = <main className="relative flex h-screen w-full justify-center">{t6}<div className="absolute z-1 my-24">{t7}<main className="min-w-[520px] max-w-3xl rounded-4xl bg-white p-14">{t9}{t10}</main></div></main>;
    $[38] = t10;
    $[39] = t9;
    $[40] = t11;
  } else {
    t11 = $[40];
  }
  return t11;
};
export default BizzCredentialsLoginPage;

/** One sign-in button per configured IdP.
 *
 * Named after the vendor when it is one a user recognises — "Sign in with Microsoft" — and after the
 * **customer** otherwise, with their logo: a company running its own federation server has no vendor
 * name worth showing, and the provider enum can't represent it anyway.
 *
 * A real `<Link>`, not a button: the SAML hop leaves the app and can be slow, so `useLinkStatus`
 * gives the click immediate feedback instead of a dead-looking form. */
const SamlButton: React.FC<{
  href: string;
  bizz: Bizz_LoginMethod.SAML['bizz'];
  /** Distinguishes IdPs when the tenant has more than one; unset otherwise. */
  label?: string | null;
  vendor?: ReturnType<typeof samlSignInVendor>;
}> = props => {
  const $ = _c(13);
  const i18n = useI18n();
  const t0 = props.href;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = buttonVariant({
      size: "lg",
      variant: "outline",
      className: "w-full gap-4"
    });
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = props.bizz;
  const t3 = props.vendor;
  let t4;
  if ($[1] !== i18n || $[2] !== props.bizz || $[3] !== props.label || $[4] !== props.vendor) {
    t4 = i18n._("auth.sign-in-with.{provider}", {
      provider: props.label ? `${props.vendor ?? props.bizz.name} — ${props.label}` : props.vendor ?? props.bizz.name
    }, {
      message: "Sign in with {provider}"
    });
    $[1] = i18n;
    $[2] = props.bizz;
    $[3] = props.label;
    $[4] = props.vendor;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== props.bizz || $[7] !== props.vendor || $[8] !== t4) {
    t5 = <SamlButtonContent bizz={t2} vendor={t3}>{t4}</SamlButtonContent>;
    $[6] = props.bizz;
    $[7] = props.vendor;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== props.href || $[11] !== t5) {
    t6 = <Link href={t0} className={t1}>{t5}</Link>;
    $[10] = props.href;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
};

/** Swaps the provider mark for a spinner while the navigation is in flight. Split out because
 * `useLinkStatus` only reports the state of an enclosing `<Link>`. */
const SamlButtonContent: React.FC<{
  bizz: Bizz_LoginMethod.SAML['bizz'];
  vendor?: ReturnType<typeof samlSignInVendor>;
  children: React.ReactNode;
}> = props => {
  const $ = _c(7);
  const {
    pending
  } = useLinkStatus();
  let t0;
  if ($[0] !== pending || $[1] !== props.bizz || $[2] !== props.vendor) {
    t0 = pending ? <Loader2 className="-m-1 size-6 shrink-0 animate-spin" aria-hidden={true} /> : <>{props.vendor === "Microsoft" && <Icon_Microsoft className="-m-1 size-6" />}{props.vendor === "Google" && <Icon_Google className="-m-1 size-6" />}{!props.vendor && props.bizz.logo && <img src={props.bizz.logo} alt="" className="-m-1 size-6 shrink-0 object-contain" />}</>;
    $[0] = pending;
    $[1] = props.bizz;
    $[2] = props.vendor;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  let t1;
  if ($[4] !== props.children || $[5] !== t0) {
    t1 = <>{t0}{props.children}</>;
    $[4] = props.children;
    $[5] = t0;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
};
const Icon_Google: React.FC<Pick<React.SVGProps<SVGSVGElement>, 'className'>> = props => {
  const $ = _c(6);
  let t0;
  let t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <path fill="#4285f4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84a10.1 10.1 0 0 1-4.4 6.65v5.52h7.12c4.16-3.83 6.56-9.47 6.56-16.18Z" />;
    t1 = <path fill="#34a853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.12-5.52c-1.97 1.32-4.49 2.1-7.44 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7A21.99 21.99 0 0 0 24 46Z" />;
    t2 = <path fill="#fbbc05" d="M11.69 28.18a13.2 13.2 0 0 1 0-8.36v-5.7H4.34a21.99 21.99 0 0 0 0 19.76l7.35-5.7Z" />;
    t3 = <path fill="#ea4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07Z" />;
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t0 = $[0];
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] !== props) {
    t4 = <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">{t0}{t1}{t2}{t3}</svg>;
    $[4] = props;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
};
const Icon_Microsoft: React.FC<Pick<React.SVGProps<SVGSVGElement>, 'className'>> = props => {
  const $ = _c(7);
  let t0;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <path fill="#f3f3f3" d="M0 0h23v23H0z" />;
    t1 = <path fill="#f35325" d="M1 1h10v10H1z" />;
    t2 = <path fill="#81bc06" d="M12 1h10v10H12z" />;
    t3 = <path fill="#05a6f0" d="M1 12h10v10H1z" />;
    t4 = <path fill="#ffba08" d="M12 12h10v10H12z" />;
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t0 = $[0];
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] !== props) {
    t5 = <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 23 23">{t0}{t1}{t2}{t3}{t4}</svg>;
    $[5] = props;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return t5;
};
function _temp(value) {
  return Boolean(value);
}
function _temp2(value_0) {
  return Boolean(value_0);
}
function _temp3(ctx) {
  return ctx.Email();
}